import type { ReportRow } from '@/api/keywordMetrics';
import type { CampaignNode } from '@/utils/entityCrossFilter';
import { campaignAdProductToReportId } from '@/api/reportAdProducts';
import {
  EMPTY_RAW_METRICS,
  accumulateInto,
  campaignLookupKey,
  rowInCampaignScope,
  rowToRaw,
  type MetricPolarity,
  type RawMetricTotals,
  type WorkspaceMetricColumnDef,
} from '@/utils/workspaceAdProductMetrics';

export const TARGETING_TYPE_IDS = [
  'keyword',
  'auto',
  'product_category',
  'audience',
  'theme',
] as const;

export type TargetingTypeId = (typeof TARGETING_TYPE_IDS)[number];

export const TARGETING_TYPE_ROWS: Array<{ id: TargetingTypeId; label: string }> = [
  { id: 'keyword', label: 'Keyword Targeting' },
  { id: 'auto', label: 'Auto Targeting' },
  { id: 'product_category', label: 'Product/Category Targeting' },
  { id: 'audience', label: 'Audience Targeting' },
  { id: 'theme', label: 'THEME Targeting' },
];

export type TargetingTypeDerivedMetrics = RawMetricTotals & {
  ctr: number;
  cpc: number;
  cr: number;
  acos: number;
  roas: number;
  asp: number;
};

export const EMPTY_TARGETING_TYPE_METRICS: TargetingTypeDerivedMetrics = {
  ...EMPTY_RAW_METRICS,
  ctr: 0,
  cpc: 0,
  cr: 0,
  acos: 0,
  roas: 0,
  asp: 0,
};

export type TargetingTypeMetricColumnDef = {
  key: keyof TargetingTypeDerivedMetrics;
  label: string;
  polarity: MetricPolarity;
  format: WorkspaceMetricColumnDef['format'];
};

/** Column order: Spend, CPC, CTR, CR%, Orders, Impressions, Clicks, Sales, ACoS, ROAS, ASP */
export const TARGETING_TYPE_METRIC_COLUMNS: TargetingTypeMetricColumnDef[] = [
  { key: 'cost', label: 'Spend', polarity: 'lower_is_better', format: 'currency' },
  { key: 'cpc', label: 'CPC', polarity: 'lower_is_better', format: 'currency' },
  { key: 'ctr', label: 'CTR', polarity: 'higher_is_better', format: 'percent' },
  { key: 'cr', label: 'CR%', polarity: 'higher_is_better', format: 'percent' },
  { key: 'purchases30d', label: 'Orders', polarity: 'higher_is_better', format: 'int' },
  { key: 'impressions', label: 'Impressions', polarity: 'higher_is_better', format: 'int' },
  { key: 'clicks', label: 'Clicks', polarity: 'higher_is_better', format: 'int' },
  { key: 'sales30d', label: 'Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'acos', label: 'ACoS', polarity: 'lower_is_better', format: 'percent' },
  { key: 'roas', label: 'ROAS', polarity: 'higher_is_better', format: 'ratio' },
  { key: 'asp', label: 'ASP', polarity: 'higher_is_better', format: 'currency' },
];

export type CampaignTargetingMeta = {
  adProduct: string;
  targetingType: string | null;
  tactic: string | null;
};

/** campaignLookupKey → targeting metadata from catalog */
export type CampaignTargetingIndex = Map<string, CampaignTargetingMeta>;

const AUTO_MATCH_TYPES = new Set([
  'CLOSE_MATCH',
  'LOOSE_MATCH',
  'SUBSTITUTES',
  'COMPLEMENTS',
  'QUERY_HIGH_REL_MATCHES',
  'QUERY_BROAD_REL_MATCHES',
  'ASIN_SUBSTITUTE_RELATED',
  'ASIN_ACCESSORY_RELATED',
  'KEYWORDS_CLOSE_MATCH',
  'KEYWORDS_LOOSE_MATCH',
  'PRODUCT_SUBSTITUTES',
  'PRODUCT_COMPLEMENTS',
  'SEARCH_CLOSE_MATCH',
  'SEARCH_LOOSE_MATCH',
]);

const KEYWORD_MATCH_TYPES = new Set(['EXACT', 'PHRASE', 'BROAD']);

function normalizeToken(value: string): string {
  return value.trim().toUpperCase().replace(/[-\s]+/g, '_');
}

export function normalizeMatchType(value: unknown): string {
  if (value == null) return '';
  return normalizeToken(String(value));
}

export function deriveTargetingTypeMetrics(raw: RawMetricTotals): TargetingTypeDerivedMetrics {
  const { impressions, clicks, cost, purchases30d, sales30d } = raw;
  return {
    impressions,
    clicks,
    cost,
    purchases30d,
    sales30d,
    ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    cpc: clicks > 0 ? cost / clicks : 0,
    cr: clicks > 0 ? (purchases30d / clicks) * 100 : 0,
    acos: sales30d > 0 ? (cost / sales30d) * 100 : 0,
    roas: cost > 0 ? sales30d / cost : 0,
    asp: purchases30d > 0 ? sales30d / purchases30d : 0,
  };
}

export function readCampaignTargetingMeta(campaign: CampaignNode): CampaignTargetingMeta {
  // CampaignNode.raw is SpCampaignNormalized; Amazon payload lives on `.raw`
  const source =
    campaign.raw && typeof campaign.raw.raw === 'object' && campaign.raw.raw !== null
      ? campaign.raw.raw
      : {};

  const targetingTypeRaw = source.targetingType ?? source.targeting_type;
  const tacticRaw = source.tactic;

  return {
    adProduct: campaignAdProductToReportId(campaign.adProduct),
    targetingType:
      targetingTypeRaw != null && String(targetingTypeRaw).trim()
        ? normalizeToken(String(targetingTypeRaw))
        : null,
    tactic:
      tacticRaw != null && String(tacticRaw).trim() ? String(tacticRaw).trim().toUpperCase() : null,
  };
}

export function buildCampaignTargetingIndex(campaigns: CampaignNode[]): CampaignTargetingIndex {
  const index: CampaignTargetingIndex = new Map();
  for (const campaign of campaigns) {
    const meta = readCampaignTargetingMeta(campaign);
    index.set(campaignLookupKey(meta.adProduct, campaign.campaignId), meta);
  }
  return index;
}

function signalsTheme(matchType: string, targetingText: string): boolean {
  if (
    matchType === 'THEME' ||
    matchType.startsWith('KEYWORDS_RELATED_TO_') ||
    matchType === 'BRAND' ||
    matchType === 'GIFT' ||
    matchType === 'CATEGORY'
  ) {
    return true;
  }
  return matchType.includes('THEME') || /theme/i.test(targetingText);
}

function signalsAudience(targetingText: string, tactic: string | null): boolean {
  const text = targetingText.toLowerCase();
  if (text.includes('audience=')) return true;
  return tactic === 'T00030' && targetingText.trim() === '';
}

function signalsViews(targetingText: string): boolean {
  return targetingText.toLowerCase().includes('views=');
}

function signalsKeywordGroup(targetingText: string): boolean {
  return targetingText.toLowerCase().includes('keyword-group=');
}

function signalsAuto(matchType: string, targetingType: string | null): boolean {
  if (targetingType === 'AUTO') return true;
  return AUTO_MATCH_TYPES.has(matchType);
}

function signalsProductCategory(matchType: string, targetingText: string): boolean {
  if (matchType.startsWith('ASIN_') && !AUTO_MATCH_TYPES.has(matchType)) return true;
  const text = targetingText.toLowerCase();
  if (text.includes('asin=') || text.includes('asin_')) return true;
  if (text.includes('category=')) return true;
  if (/asin[_-]?(same|category|expanded|brand|price|review)/i.test(matchType)) return true;
  return false;
}

function signalsKeyword(matchType: string): boolean {
  return KEYWORD_MATCH_TYPES.has(matchType);
}

/**
 * Classify a report row into exactly one targeting bucket.
 * Priority: Theme → audience= → views= remarketing → keyword-group → Auto
 * → Product/Category → Keyword.
 * Keyword groups (`keyword-group=`) are Product/Category and run before Auto.
 * Views must run before product/category because views=(category="…") contains category=.
 * Returns null when the row cannot be attributed.
 */
export function classifyTargetingType(
  row: ReportRow,
  campaignIndex: CampaignTargetingIndex
): TargetingTypeId | null {
  const campaignId = row.campaignId ? String(row.campaignId) : '';
  const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
  if (!campaignId) return null;

  const meta = campaignIndex.get(campaignLookupKey(adProduct, campaignId));
  const matchType = normalizeMatchType(row.matchType);
  const targetingText = row.targetingText ? String(row.targetingText) : '';
  const targetingType = meta?.targetingType ?? null;
  const tactic = meta?.tactic ?? null;
  const isTargetRow = Boolean(row.targetId) || Boolean(targetingText);

  if (adProduct === 'sponsored_brands' && signalsTheme(matchType, targetingText)) {
    return 'theme';
  }
  if (signalsTheme(matchType, targetingText)) {
    return 'theme';
  }

  if (signalsAudience(targetingText, tactic)) {
    return 'audience';
  }

  // Must run before product/category: views=(category="…") contains category=.
  if (signalsViews(targetingText)) {
    return 'audience';
  }

  // Keyword groups are Product/Category. Before Auto so AUTO campaigns cannot swallow them.
  if (signalsKeywordGroup(targetingText)) {
    return 'product_category';
  }

  if (signalsAuto(matchType, targetingType)) {
    return 'auto';
  }

  if (signalsProductCategory(matchType, targetingText)) {
    return 'product_category';
  }

  // SD target rows without audience signals → product/category (context targeting)
  if (isTargetRow && adProduct === 'sponsored_display') {
    return 'product_category';
  }

  // SP/SB expression/target rows without stronger signals → product/category
  if (isTargetRow && (adProduct === 'sponsored_products' || adProduct === 'sponsored_brands')) {
    return 'product_category';
  }

  if (signalsKeyword(matchType)) {
    return 'keyword';
  }

  // Keyword-shaped rows without match type on MANUAL campaigns still count as keyword
  if (!isTargetRow && targetingType !== 'AUTO' && (row.keywordId || row.keywordText)) {
    return 'keyword';
  }

  return null;
}

function finalizeTargetingTypeMap(
  map: Map<string, RawMetricTotals>
): Map<TargetingTypeId, TargetingTypeDerivedMetrics> {
  const out = new Map<TargetingTypeId, TargetingTypeDerivedMetrics>();
  for (const id of TARGETING_TYPE_IDS) {
    const raw = map.get(id) ?? { ...EMPTY_RAW_METRICS };
    out.set(id, deriveTargetingTypeMetrics(raw));
  }
  return out;
}

export function aggregateTargetingTypeMetrics(
  keywordRows: ReportRow[],
  targetRows: ReportRow[],
  campaignIndex: CampaignTargetingIndex,
  scopedCampaignLookupKeys: Set<string> | null
): Map<TargetingTypeId, TargetingTypeDerivedMetrics> {
  const rawByType = new Map<string, RawMetricTotals>();
  for (const id of TARGETING_TYPE_IDS) {
    rawByType.set(id, { ...EMPTY_RAW_METRICS });
  }

  const ingest = (row: ReportRow, defaultAdProduct: string) => {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    const adProduct = row.adProduct ? String(row.adProduct) : defaultAdProduct;
    if (!campaignId || !rowInCampaignScope(adProduct, campaignId, scopedCampaignLookupKeys)) {
      return;
    }
    const bucket = classifyTargetingType(
      { ...row, adProduct },
      campaignIndex
    );
    if (!bucket) return;
    accumulateInto(rawByType, bucket, rowToRaw(row));
  };

  for (const row of keywordRows) {
    ingest(row, 'sponsored_products');
  }
  for (const row of targetRows) {
    ingest(row, row.adProduct ? String(row.adProduct) : 'sponsored_display');
  }

  return finalizeTargetingTypeMap(rawByType);
}

export type TargetingTypeSummaryMetrics = {
  impressions: number;
  clicks: number;
  cost: number;
  purchases30d: number;
  sales30d: number;
  ctr: number;
  cpc: number;
  cr: number;
  acos: number;
  roas: number;
  asp: number;
};

export type TargetingTypeSummaryBucket = {
  id: string;
  label: string;
  current: TargetingTypeSummaryMetrics;
  previous: TargetingTypeSummaryMetrics | null;
};

export type TargetingTypeSummaryPayload = {
  buckets?: TargetingTypeSummaryBucket[];
  unclassified?: {
    current?: Partial<TargetingTypeSummaryMetrics> | null;
    previous?: Partial<TargetingTypeSummaryMetrics> | null;
  };
  totals?: {
    current?: TargetingTypeSummaryMetrics | null;
    previous?: TargetingTypeSummaryMetrics | null;
  };
};

function coerceSummaryMetrics(
  value: TargetingTypeSummaryMetrics | null | undefined
): TargetingTypeDerivedMetrics {
  if (!value) return { ...EMPTY_TARGETING_TYPE_METRICS };
  return {
    impressions: Number(value.impressions) || 0,
    clicks: Number(value.clicks) || 0,
    cost: Number(value.cost) || 0,
    purchases30d: Number(value.purchases30d) || 0,
    sales30d: Number(value.sales30d) || 0,
    ctr: Number(value.ctr) || 0,
    cpc: Number(value.cpc) || 0,
    cr: Number(value.cr) || 0,
    acos: Number(value.acos) || 0,
    roas: Number(value.roas) || 0,
    asp: Number(value.asp) || 0,
  };
}

function unclassifiedToRaw(
  value: Partial<TargetingTypeSummaryMetrics> | null | undefined
): TargetingTypeDerivedMetrics {
  return deriveTargetingTypeMetrics({
    impressions: Number(value?.impressions) || 0,
    clicks: Number(value?.clicks) || 0,
    cost: Number(value?.cost) || 0,
    purchases30d: Number(value?.purchases30d) || 0,
    sales30d: Number(value?.sales30d) || 0,
  });
}

/** Map backend targeting-type summary payload into Workspace table maps. */
export function targetingTypeMapsFromSummary(
  data: TargetingTypeSummaryPayload | null | undefined
): {
  current: Map<TargetingTypeId, TargetingTypeDerivedMetrics>;
  previous: Map<TargetingTypeId, TargetingTypeDerivedMetrics>;
  currentTotals: TargetingTypeDerivedMetrics;
  previousTotals: TargetingTypeDerivedMetrics;
} {
  const current = new Map<TargetingTypeId, TargetingTypeDerivedMetrics>();
  const previous = new Map<TargetingTypeId, TargetingTypeDerivedMetrics>();
  for (const id of TARGETING_TYPE_IDS) {
    current.set(id, { ...EMPTY_TARGETING_TYPE_METRICS });
    previous.set(id, { ...EMPTY_TARGETING_TYPE_METRICS });
  }
  for (const bucket of data?.buckets ?? []) {
    if (!TARGETING_TYPE_IDS.includes(bucket.id as TargetingTypeId)) continue;
    const id = bucket.id as TargetingTypeId;
    current.set(id, coerceSummaryMetrics(bucket.current));
    previous.set(id, coerceSummaryMetrics(bucket.previous));
  }
  const currentTotals = data?.totals?.current
    ? coerceSummaryMetrics(data.totals.current)
    : deriveTargetingTypeMetrics(
        [...current.values(), unclassifiedToRaw(data?.unclassified?.current)].reduce(
          (acc, row) => ({
            impressions: acc.impressions + row.impressions,
            clicks: acc.clicks + row.clicks,
            cost: acc.cost + row.cost,
            purchases30d: acc.purchases30d + row.purchases30d,
            sales30d: acc.sales30d + row.sales30d,
          }),
          { impressions: 0, clicks: 0, cost: 0, purchases30d: 0, sales30d: 0 }
        )
      );
  const previousTotals = data?.totals?.previous
    ? coerceSummaryMetrics(data.totals.previous)
    : deriveTargetingTypeMetrics(
        [...previous.values(), unclassifiedToRaw(data?.unclassified?.previous)].reduce(
          (acc, row) => ({
            impressions: acc.impressions + row.impressions,
            clicks: acc.clicks + row.clicks,
            cost: acc.cost + row.cost,
            purchases30d: acc.purchases30d + row.purchases30d,
            sales30d: acc.sales30d + row.sales30d,
          }),
          { impressions: 0, clicks: 0, cost: 0, purchases30d: 0, sales30d: 0 }
        )
      );
  return { current, previous, currentTotals, previousTotals };
}

export function campaignKeysQueryParam(keys: Set<string> | null | undefined): string | undefined {
  if (!keys || keys.size === 0) return undefined;
  return [...keys].sort().join(',');
}
